import { type ChangeEvent, useRef, useState } from 'react'

import { t } from 'commons/libs/i18n'
import { fileTypes } from 'commons/utils/file'
import { Flex, Icon, Modal, SlimButton, Text } from 'components'
import { toast } from 'react-toastify'
import { useAppTheme } from 'styles/theme'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

export interface UploadModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  title: string
  description?: string
  downloadStepLabel?: string
  uploadStepLabel?: string
  templateUrl?: string
  accept?: string
  onConfirm: (file: File) => void
  onReturn?: () => void
}

export const UploadModal = ({
  open,
  setOpen,
  title,
  description,
  downloadStepLabel = t('components.uploadModal.stepOne'),
  uploadStepLabel = t('components.uploadModal.stepTwo'),
  templateUrl,
  accept = '',
  onConfirm,
  onReturn,
}: UploadModalProps) => {
  const { colors } = useAppTheme()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClose = () => {
    setOpen(false)
    setSelectedFile(null)
  }

  const handleReturn = () => {
    if (onReturn) {
      onReturn()
    } else {
      handleClose()
    }
  }

  const handleUploadClick = () => {
    inputRef.current?.click()
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (accept && file.type !== accept) {
        if (accept === fileTypes.Excel) {
          toast.error(t('common.errors.fileMustBeSpreadsheet'))
        } else {
          toast.error(t('common.errors.fileIsNotSupported'))
        }
        return
      }

      setSelectedFile(file)
    }

    // Reset the input value so the same file can be re-selected after clearing
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleClearFile = () => {
    setSelectedFile(null)
  }

  const handleConfirm = () => {
    if (selectedFile) {
      onConfirm(selectedFile)
      handleClearFile()
      setOpen(false)
    }
  }

  const handleDownloadClick = () => {
    toast.info(t('common.messages.downloadStarting'))
    window.open(templateUrl, '_blank')
    toast.success(t('common.messages.spreadsheetDownloadSuccess'))
  }

  return (
    <Modal
      buttonAction={handleConfirm}
      buttonDisabled={!selectedFile}
      buttonText={t('buttons.confirm')}
      closeAction={handleClose}
      modalWidth={600}
      open={open}
      returnAction={handleReturn}
      returnText={t('buttons.return')}
      setOpen={setOpen}
      showAction
      showClose
      showReturn
      title={title}
      titleSize={18}
    >
      <Flex direction="column" fullWidth gap="md">
        <S.HiddenInput accept={accept} onChange={handleFileChange} ref={inputRef} type="file" />

        <Text color="stronger" size="xs">
          {description}
        </Text>

        <S.StepsContainer>
          {templateUrl && (
            <Flex alignItems="normal" direction="column" fullWidth gap="xs">
              <Text customColor={colors.ion.primary.main} size="2xs">
                {downloadStepLabel}
              </Text>
              <SlimButton
                content={t('components.uploadModal.downloadModelSpreadsheet')}
                onClick={handleDownloadClick}
                size="sm"
                variant="info"
              />
            </Flex>
          )}

          <Flex alignItems="normal" direction="column" fullWidth gap="xs">
            <Text customColor={colors.ion.primary.main} size="2xs">
              {uploadStepLabel}
            </Text>

            {selectedFile ? (
              <Flex alignItems="normal" direction="row" fullWidth gap="xs">
                <S.UploadedFileRow>
                  <Icon color={theme.colors.primary.main} icon="paper-clip" size="15" />
                  <S.FileNameText>{selectedFile.name}</S.FileNameText>
                </S.UploadedFileRow>

                <SlimButton
                  content={t('buttons.change')}
                  onClick={handleUploadClick}
                  size="sm"
                  variant="info"
                />
                <SlimButton
                  icon={<Icon icon="trash" size="16" />}
                  onClick={handleClearFile}
                  size="sm"
                  variant="info"
                />
              </Flex>
            ) : (
              <SlimButton
                content={t('components.uploadModal.uploadFilledSpreadsheet')}
                onClick={handleUploadClick}
                size="sm"
                variant="info"
              />
            )}
          </Flex>
        </S.StepsContainer>
      </Flex>
    </Modal>
  )
}
